/**
 * Pattern Detection Service
 * Analyzes meal data to detect patterns without requiring user input
 * Also integrates with AI-generated glycemic insights
 */

import { isThisWeek, getHours, subDays, format, startOfWeek, endOfWeek, isBefore, isAfter } from 'date-fns';
import AsyncStorage from '@/lib/asyncStorage';
import { CloudAI, type GlycemicInsight } from './cloud-ai';
import { Firebase } from './firebase/init';
import type { Meal } from '@/types';
import type { WeeklyStats } from './stats';

export type PatternType = 'warning' | 'success' | 'info';

// Insight type for AI-generated insights (pre-translated)
export type InsightType = 'trigger' | 'safe' | 'stacking' | 'timing' | 'trend' | 'swap_opportunity' | 'tip'
  | 'achievement' | 'fiber_insight' | 'sugar_insight' | 'target_progress' | 'meal_suggestion' | 'medication_aware'
  | 'glucose_pattern' | 'hydration' | 'blood_pressure' | 'weight_trend' | 'micronutrient';

// New unified insight format (supports both AI and local patterns)
export interface Insight {
  type: InsightType | PatternType;
  section?: 'highlights' | 'patterns' | 'nutrition' | 'health' | 'recommendations';
  icon: string;
  title: string;       // Direct text (for AI insights)
  description: string; // Direct text (for AI insights)
  titleKey?: string;       // Translation key (for local fallback)
  descriptionKey?: string; // Translation key (for local fallback)
  descriptionParams?: Record<string, string | number>;
  confidence?: 'high' | 'medium' | 'low';
  source: 'ai' | 'local';
}

// Cache key for insights
const GLYCEMIC_INSIGHTS_CACHE_KEY = 'glycemic_insights_v2';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

/** Per-user cache key so one account's insights never leak to another */
function getGlycemicInsightsCacheKey(uid: string): string {
  return `${GLYCEMIC_INSIGHTS_CACHE_KEY}_${uid}`;
}

interface CachedInsights {
  summary?: string;
  insights: Insight[];
  mealsHash: string;
  cachedAt: number;
}

// Result type for getGlycemicInsights
export interface GlycemicInsightsResult {
  summary?: string;
  insights: Insight[];
}

/**
 * Check if a date is in last week
 */
function isLastWeek(date: Date, options?: { weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 }): boolean {
  const now = new Date();
  const lastWeekStart = startOfWeek(subDays(now, 7), options);
  const lastWeekEnd = endOfWeek(subDays(now, 7), options);
  return isAfter(date, lastWeekStart) && isBefore(date, lastWeekEnd);
}

export interface Pattern {
  type: PatternType;
  icon: string;
  titleKey: string;
  descriptionKey: string;
  descriptionParams?: Record<string, string | number>;
}

/**
 * Count food frequency across meals
 */
function countFoodFrequency(meals: Meal[]): Record<string, number> {
  const frequency: Record<string, number> = {};

  meals.forEach(meal => {
    if (!meal.glycemicAnalysis?.identifiedFoods) return;

    meal.glycemicAnalysis.identifiedFoods.forEach(food => {
      const name = food.name.toLowerCase();
      frequency[name] = (frequency[name] || 0) + 1;
    });
  });

  return frequency;
}

/**
 * Calculate average score from meals
 */
function average(numbers: number[]): number {
  if (numbers.length === 0) return 0;
  return numbers.reduce((a, b) => a + b, 0) / numbers.length;
}

/**
 * Detect patterns from meal data (no user input needed)
 */
export function detectPatterns(meals: Meal[], stats: WeeklyStats): Pattern[] {
  const patterns: Pattern[] = [];

  // Filter to analyzed meals
  const analyzedMeals = meals.filter(m => m.glycemicAnalysis);

  if (analyzedMeals.length < 3) {
    // Not enough data for meaningful patterns
    return patterns;
  }

  // 1. Repeat offenders - foods that appear in multiple high-GI meals
  const highGIMeals = analyzedMeals.filter(m => m.glycemicAnalysis!.glucoseImpactScore >= 70);
  const foodFrequency = countFoodFrequency(highGIMeals);
  const repeatOffenders = Object.entries(foodFrequency)
    .filter(([, count]) => count >= 3)
    .sort((a, b) => b[1] - a[1]);

  if (repeatOffenders.length > 0) {
    const [food, count] = repeatOffenders[0];
    const capitalizedFood = food.charAt(0).toUpperCase() + food.slice(1);
    patterns.push({
      type: 'warning',
      icon: '⚠️',
      titleKey: 'patterns.repeatOffender',
      descriptionKey: 'patterns.descriptions.repeatOffender',
      descriptionParams: { food: capitalizedFood, count },
    });
  }

  // 2. Safe foods - foods that appear in multiple low-GI meals
  const lowGIMeals = analyzedMeals.filter(m => m.glycemicAnalysis!.glucoseImpactScore <= 40);
  const safeFoodFrequency = countFoodFrequency(lowGIMeals);
  const safeFoods = Object.entries(safeFoodFrequency)
    .filter(([, count]) => count >= 3)
    .sort((a, b) => b[1] - a[1]);

  if (safeFoods.length > 0) {
    const topSafe = safeFoods
      .slice(0, 3)
      .map(([food]) => food.charAt(0).toUpperCase() + food.slice(1));
    patterns.push({
      type: 'success',
      icon: '✅',
      titleKey: 'patterns.safeChoices',
      descriptionKey: 'patterns.descriptions.safeChoices',
      descriptionParams: { foods: topSafe.join(', ') },
    });
  }

  // 3. Meal timing patterns
  const breakfasts = analyzedMeals.filter(m => {
    const date = m.eatenAt instanceof Date ? m.eatenAt : new Date(m.eatenAt);
    return getHours(date) < 11;
  });

  const dinners = analyzedMeals.filter(m => {
    const date = m.eatenAt instanceof Date ? m.eatenAt : new Date(m.eatenAt);
    return getHours(date) >= 17;
  });

  if (breakfasts.length >= 3 && dinners.length >= 3) {
    const avgBreakfast = average(breakfasts.map(m => m.glycemicAnalysis!.glucoseImpactScore));
    const avgDinner = average(dinners.map(m => m.glycemicAnalysis!.glucoseImpactScore));

    if (avgBreakfast > avgDinner + 15) {
      patterns.push({
        type: 'info',
        icon: '🌅',
        titleKey: 'patterns.breakfastAlert',
        descriptionKey: 'patterns.descriptions.breakfastAlert',
        descriptionParams: { breakfast: Math.round(avgBreakfast), dinner: Math.round(avgDinner) },
      });
    } else if (avgDinner > avgBreakfast + 15) {
      patterns.push({
        type: 'info',
        icon: '🌙',
        titleKey: 'patterns.dinnerAlert',
        descriptionKey: 'patterns.descriptions.dinnerAlert',
        descriptionParams: { dinner: Math.round(avgDinner), breakfast: Math.round(avgBreakfast) },
      });
    }
  }

  // 4. Improvement trend
  const thisWeekMeals = analyzedMeals.filter(m => {
    const date = m.eatenAt instanceof Date ? m.eatenAt : new Date(m.eatenAt);
    return isThisWeek(date, { weekStartsOn: 1 });
  });

  const lastWeekMeals = analyzedMeals.filter(m => {
    const date = m.eatenAt instanceof Date ? m.eatenAt : new Date(m.eatenAt);
    return isLastWeek(date, { weekStartsOn: 1 });
  });

  if (thisWeekMeals.length >= 3 && lastWeekMeals.length >= 3) {
    const thisWeekAvg = average(thisWeekMeals.map(m => m.glycemicAnalysis!.glucoseImpactScore));
    const lastWeekAvg = average(lastWeekMeals.map(m => m.glycemicAnalysis!.glucoseImpactScore));

    if (thisWeekAvg < lastWeekAvg - 5) {
      const improvement = Math.round(((lastWeekAvg - thisWeekAvg) / lastWeekAvg) * 100);
      patterns.push({
        type: 'success',
        icon: '📈',
        titleKey: 'patterns.improving',
        descriptionKey: 'patterns.descriptions.improving',
        descriptionParams: { percent: improvement },
      });
    } else if (thisWeekAvg > lastWeekAvg + 10) {
      const increase = Math.round(((thisWeekAvg - lastWeekAvg) / lastWeekAvg) * 100);
      patterns.push({
        type: 'warning',
        icon: '📉',
        titleKey: 'patterns.watchOut',
        descriptionKey: 'patterns.descriptions.watchOut',
        descriptionParams: { percent: increase },
      });
    }
  }

  // 5. GI stacking warning - multiple high-GI foods in same meal
  const stackedMeals = analyzedMeals.filter(m => {
    if (!m.glycemicAnalysis?.identifiedFoods) return false;
    const highGIFoods = m.glycemicAnalysis.identifiedFoods.filter(f => (f.giValue ?? 0) >= 70);
    return highGIFoods.length >= 2;
  });

  if (stackedMeals.length >= 2) {
    patterns.push({
      type: 'warning',
      icon: '📚',
      titleKey: 'patterns.giStacking',
      descriptionKey: 'patterns.descriptions.giStacking',
      descriptionParams: { count: stackedMeals.length },
    });
  }

  // 6. Low-GI streak encouragement
  if (stats.currentStreak >= 3) {
    patterns.push({
      type: 'success',
      icon: '🔥',
      titleKey: 'patterns.onFire',
      descriptionKey: 'patterns.descriptions.onFire',
      descriptionParams: { days: stats.currentStreak },
    });
  }

  // 7. Consistency pattern
  const scores = analyzedMeals.map(m => m.glycemicAnalysis!.glucoseImpactScore);
  if (scores.length >= 5) {
    const variance = calculateVariance(scores);
    if (variance < 100) { // Low variance = consistent choices
      patterns.push({
        type: 'success',
        icon: '🎯',
        titleKey: 'patterns.consistent',
        descriptionKey: 'patterns.descriptions.consistent',
      });
    } else if (variance > 500) { // High variance = erratic choices
      patterns.push({
        type: 'info',
        icon: '🎢',
        titleKey: 'patterns.variable',
        descriptionKey: 'patterns.descriptions.variable',
      });
    }
  }

  // Return top 5 patterns max
  return patterns.slice(0, 5);
}

/**
 * Calculate variance of a set of numbers
 */
function calculateVariance(numbers: number[]): number {
  if (numbers.length === 0) return 0;
  const mean = average(numbers);
  const squaredDiffs = numbers.map(n => Math.pow(n - mean, 2));
  return average(squaredDiffs);
}

/**
 * Get the color for a pattern type
 */
export function getPatternBackgroundColor(type: PatternType, isDark: boolean): string {
  if (isDark) {
    switch (type) {
      case 'warning': return 'rgba(239, 68, 68, 0.15)';
      case 'success': return 'rgba(34, 197, 94, 0.15)';
      case 'info': return 'rgba(59, 130, 246, 0.15)';
    }
  }

  switch (type) {
    case 'warning': return '#FEF2F2';
    case 'success': return '#F0FDF4';
    case 'info': return '#EFF6FF';
  }
}

/**
 * Get the border color for a pattern type
 */
export function getPatternBorderColor(type: PatternType): string {
  switch (type) {
    case 'warning': return '#EF4444';
    case 'success': return '#22C55E';
    case 'info': return '#3B82F6';
  }
}

// ============================================
// Insight Type to Pattern Type Mapping
// ============================================

/**
 * Map AI insight types to pattern types for styling
 */
export function insightTypeToPatternType(type: InsightType | PatternType): PatternType {
  switch (type) {
    case 'trigger':
    case 'stacking':
    case 'sugar_insight':
      return 'warning';
    case 'safe':
    case 'trend':
    case 'swap_opportunity':
    case 'achievement':
    case 'fiber_insight':
    case 'target_progress':
      return 'success';
    case 'timing':
    case 'tip':
    case 'meal_suggestion':
    case 'medication_aware':
    default:
      return 'info';
  }
}

/**
 * Get background color for an insight (handles both AI and local types)
 */
export function getInsightBackgroundColor(type: InsightType | PatternType, isDark: boolean): string {
  const patternType = ['warning', 'success', 'info'].includes(type as PatternType)
    ? type as PatternType
    : insightTypeToPatternType(type as InsightType);
  return getPatternBackgroundColor(patternType, isDark);
}

/**
 * Get border color for an insight (handles both AI and local types)
 */
export function getInsightBorderColor(type: InsightType | PatternType): string {
  const patternType = ['warning', 'success', 'info'].includes(type as PatternType)
    ? type as PatternType
    : insightTypeToPatternType(type as InsightType);
  return getPatternBorderColor(patternType);
}

// ============================================
// AI Insights Integration
// ============================================

/**
 * Convert AI-generated insights to unified Insight format
 */
function convertAIInsights(aiInsights: GlycemicInsight[]): Insight[] {
  return aiInsights.map(insight => ({
    type: insight.type,
    section: insight.section,
    icon: insight.emoji,
    title: insight.title,
    description: insight.description,
    confidence: insight.confidence,
    source: 'ai' as const,
  }));
}

/**
 * Convert local patterns to unified Insight format
 * This uses translation keys, so the caller must translate them
 */
function convertPatternsToInsights(patterns: Pattern[]): Insight[] {
  return patterns.map(pattern => ({
    type: pattern.type,
    icon: pattern.icon,
    title: '', // Will be filled by translation
    description: '', // Will be filled by translation
    titleKey: pattern.titleKey,
    descriptionKey: pattern.descriptionKey,
    descriptionParams: pattern.descriptionParams,
    source: 'local' as const,
  }));
}

/**
 * Generate cache key based on meal data
 */
function generateMealsHash(meals: Meal[]): string {
  const analyzedCount = meals.filter(m => m.glycemicAnalysis).length;
  const latestMealId = meals[0]?.id || '';
  return `${meals.length}-${analyzedCount}-${latestMealId}`;
}

/**
 * Get glycemic insights - tries AI first, falls back to local patterns
 * Results are cached for 24 hours or until meals change
 */
export async function getGlycemicInsights(
  meals: Meal[],
  stats: WeeklyStats,
  userLanguage: string = 'English',
  forceRefresh: boolean = false
): Promise<GlycemicInsightsResult> {
  const mealsHash = generateMealsHash(meals);
  const uid = Firebase.auth.currentUser?.uid || null;
  const storageKey = uid ? getGlycemicInsightsCacheKey(uid) : null;

  // Check cache first (unless force refresh)
  if (!forceRefresh && storageKey) {
    try {
      const cached = await AsyncStorage.getItem(storageKey);
      if (cached) {
        const parsedCache: CachedInsights = JSON.parse(cached);
        const cacheAge = Date.now() - parsedCache.cachedAt;

        if (cacheAge < CACHE_DURATION && parsedCache.mealsHash === mealsHash) {
          return { summary: parsedCache.summary, insights: parsedCache.insights };
        }
      }
    } catch (error) {
      console.warn('Error reading insights cache:', error);
    }
  }

  // Try AI-generated insights (cloud function reads diary entries server-side)
  try {
    console.log('[Insights] Calling generateGlycemicInsights with language:', userLanguage);
    const response = await CloudAI.generateGlycemicInsights({
      userLanguage,
      days: 30,
      limit: 50,
    });

    if (response.success && response.insights.length > 0 && !response.useLocal) {
      console.log('[Insights] Using AI insights:', response.insights.length);
      const insights = convertAIInsights(response.insights);
      const summary = response.summary;

      // Cache the results (only when we know which user they belong to)
      if (storageKey) {
        try {
          const cacheData: CachedInsights = {
            summary,
            insights,
            mealsHash,
            cachedAt: Date.now(),
          };
          await AsyncStorage.setItem(storageKey, JSON.stringify(cacheData));
        } catch (error) {
          console.warn('Error caching insights:', error);
        }
      }

      return { summary, insights };
    }
  } catch (error) {
    console.warn('[Insights] AI insights failed, falling back to local:', error);
  }

  // Fallback to local pattern detection
  console.log('[Insights] Using local pattern detection fallback');
  const localPatterns = detectPatterns(meals, stats);
  const insights = convertPatternsToInsights(localPatterns);

  // Cache local insights too (only when we know which user they belong to)
  if (storageKey) {
    try {
      const cacheData: CachedInsights = {
        insights,
        mealsHash,
        cachedAt: Date.now(),
      };
      await AsyncStorage.setItem(storageKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error caching local insights:', error);
    }
  }

  return { insights };
}

/**
 * Clear the glycemic insights cache
 * @param uid - User whose cache to clear; defaults to the current user
 */
export async function clearGlycemicInsightsCache(uid?: string): Promise<void> {
  try {
    const targetUid = uid || Firebase.auth.currentUser?.uid;
    const keys: string[] = [GLYCEMIC_INSIGHTS_CACHE_KEY]; // legacy unscoped key
    if (targetUid) keys.push(getGlycemicInsightsCacheKey(targetUid));
    await AsyncStorage.multiRemove(keys);
  } catch (error) {
    console.warn('Error clearing insights cache:', error);
  }
}
